import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from '@headlessui/react';
import {CheckIcon, ChevronUpDownIcon} from '@heroicons/react/20/solid';
import {PhotoIcon} from '@heroicons/react/24/solid';
import React, {ReactNode, useCallback, useEffect, useId, useState} from 'react';

import {Badge} from '../Badge';
import classNames from 'classnames';
import {FormSectionItem, IFormSectionItemProps} from './FormSection';
import {getAllFileEntries} from '../../../utils/FileUtils';

const TEXT_COLOR = 'text-gray-900 placeholder:text-gray-400';
const ERROR_TEXT_COLOR = 'text-red-900 placeholder:text-red-300';
const DISABLED_TEXT_COLOR =
  'text-gray-500 placeholder:text-gray-500 disabled:text-gray-500 ';

const BASE_INPUT_STYLE =
  'block w-full rounded-md border-0 py-1.5 shadow-sm ring-1 ring-inset focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6';

const DEFAULT_CHECKBOX_STYLE =
  'h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-600 disabled:ring-gray-300 disabled:text-gray-600';

export const DEFAULT_INPUT_STYLE = classNames(
  BASE_INPUT_STYLE,
  TEXT_COLOR,
  'ring-gray-300 focus:ring-blue-600',
);
export const ERROR_INPUT_STYLE = classNames(
  BASE_INPUT_STYLE,
  ERROR_TEXT_COLOR,
  'ring-red-500 focus:ring-red-500',
);
export const DISABLED_INPUT_STYLE = classNames(
  BASE_INPUT_STYLE,
  DISABLED_TEXT_COLOR,
  'ring-gray-300 focus:ring-gray-300 read-only:cursor-not-allowed read-only:bg-gray-50 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:ring-gray-200',
);

interface IInputProps
  extends Omit<IFormItemContainerProps, 'id' | 'children' | 'disabled'> {
  inputProps: React.InputHTMLAttributes<HTMLInputElement>;
}

export const Input: React.FC<IInputProps> = (props) => {
  const fallbackId = useId();
  const id = props.inputProps.id ?? fallbackId;
  const errorId =
    props.errors && props.errors.length > 0 ? `${id}-error` : undefined;
  return (
    <FormItemContainer
      id={id}
      {...props}
      disabled={props.inputProps.disabled || props.inputProps.readOnly}>
      <input
        id={id}
        aria-invalid={errorId ? true : undefined}
        aria-describedby={errorId}
        className={
          errorId
            ? ERROR_INPUT_STYLE
            : props.inputProps.disabled === true ||
                props.inputProps.readOnly === true
              ? props.inputProps.type === 'checkbox'
                ? DEFAULT_CHECKBOX_STYLE
                : DISABLED_INPUT_STYLE
              : props.inputProps.type === 'checkbox'
                ? DEFAULT_CHECKBOX_STYLE
                : DEFAULT_INPUT_STYLE
        }
        {...props.inputProps}
      />
    </FormItemContainer>
  );
};

export const ColorInput: React.FC<IInputProps> = (props) => {
  const fallbackId = useId();
  const id = props.inputProps.id ?? fallbackId;
  const errorId =
    props.errors && props.errors.length > 0 ? `${id}-error` : undefined;
  return (
    <FormItemContainer
      id={id}
      {...props}
      disabled={props.inputProps.disabled || props.inputProps.readOnly}>
      <div className="relative flex gap-x-2">
        <div className="absolute inset-y-0 left-0 flex items-center pl-2">
          <input
            id={`${id}-color`}
            className="h-7 w-7 bg-transparent"
            aria-invalid={errorId ? true : undefined}
            aria-describedby={errorId}
            {...props.inputProps}
            type="color"
          />
        </div>
        <input
          id={id}
          aria-invalid={errorId ? true : undefined}
          aria-describedby={errorId}
          className={classNames(
            errorId
              ? ERROR_INPUT_STYLE
              : props.inputProps.disabled === true ||
                  props.inputProps.readOnly === true
                ? DISABLED_INPUT_STYLE
                : DEFAULT_INPUT_STYLE,
            'pl-10',
          )}
          {...props.inputProps}
        />
      </div>
    </FormItemContainer>
  );
};
interface IFileInputProps
  extends Omit<IFormItemContainerProps, 'id' | 'children' | 'disabled'> {
  isDirectory?: boolean;
  inputProps: React.InputHTMLAttributes<HTMLInputElement>;
  compact?: boolean;
  onFilesSelected?: (files: File[]) => void;
}

export const FileInput: React.FC<IFileInputProps> = (props) => {
  const fallbackId = useId();
  const {isDirectory, compact = false} = props;
  const id = props.inputProps.id ?? fallbackId;

  const _onFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files) {
      console.log('No selected files!');
      return;
    }
    const arrFiles = Array.from(event.target.files);
    props.onFilesSelected?.(arrFiles);
  };

  const _onDropFiles = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const dataTransferItems = e.dataTransfer.items;
    let arrFiles: File[] = [];
    try {
      arrFiles = await getAllFileEntries(dataTransferItems);
    } catch (err) {
      console.error(err);
    }
    props.onFilesSelected?.(arrFiles);
  };

  const _onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const _inputCallback = useCallback(
    (node: any) => {
      if (node && isDirectory) {
        node.directory = true;
        node.webkitdirectory = true;
      }
    },
    [isDirectory],
  );
  // const errorId =
  //   props.errors && props.errors.length > 0 ? `${id}-error` : undefined;
  return (
    <FormItemContainer
      id={id}
      {...props}
      disabled={props.inputProps.disabled || props.inputProps.readOnly}>
      <div
        onDrop={_onDropFiles}
        onDragOver={_onDragOver}
        className={classNames(
          'mt-2 flex justify-center rounded-lg border border-dashed border-gray-900/25',
          compact ? 'px-6 py-1.5' : 'px-6 py-10',
        )}>
        <div className="text-center">
          {!compact && (
            <PhotoIcon
              aria-hidden="true"
              className="mx-auto h-12 w-12 text-gray-300"
            />
          )}
          <div className="flex text-sm leading-6 text-gray-600">
            <label
              htmlFor={id}
              className={classNames(
                props.inputProps.disabled || props.inputProps.readOnly
                  ? 'text-gray-600'
                  : 'cursor-pointer text-blue-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-blue-600 focus-within:ring-offset-2 hover:text-blue-500',
                'relative rounded-md bg-white font-semibold ',
              )}>
              <span>Select a folder</span>
              <input
                ref={_inputCallback}
                id={id}
                name="file-upload"
                type="file"
                className="sr-only"
                {...props.inputProps}
                onSubmit={_onFileInputChange}
                onChange={_onFileInputChange}
              />
            </label>
            {!compact && <p className="pl-1">or drag and drop</p>}
          </div>
          {!compact && (
            <p className="text-xs leading-5 text-gray-600">
              PNG, JPG, GIF files
            </p>
          )}
        </div>
      </div>
    </FormItemContainer>
  );
};

interface ICheckboxProps
  extends Omit<IFormItemContainerProps, 'id' | 'children' | 'disabled'> {
  inputProps: React.InputHTMLAttributes<HTMLInputElement>;
}

export const Checkbox: React.FC<ICheckboxProps> = (props) => {
  const fallbackId = useId();
  const id = props.inputProps.id ?? fallbackId;
  const errorId =
    props.errors && props.errors.length > 0 ? `${id}-error` : undefined;
  return (
    <FormItemContainer
      id={id}
      {...props}
      disabled={props.inputProps.disabled || props.inputProps.readOnly}>
      <input
        id={id}
        aria-invalid={errorId ? true : undefined}
        aria-describedby={errorId}
        type="checkbox"
        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-600"
        {...props.inputProps}
      />
    </FormItemContainer>
  );
};

interface ITextAreaInputProps
  extends Omit<IFormItemContainerProps, 'id' | 'children' | 'disabled'> {
  inputProps: React.TextareaHTMLAttributes<HTMLTextAreaElement>;
}

export const TextareaInput: React.FC<ITextAreaInputProps> = (props) => {
  const fallbackId = useId();
  const id = props.inputProps.id ?? fallbackId;
  const errorId =
    props.errors && props.errors.length > 0 ? `${id}-error` : undefined;
  return (
    <FormItemContainer
      id={id}
      {...props}
      disabled={props.inputProps.disabled || props.inputProps.readOnly}>
      <textarea
        id={id}
        aria-invalid={errorId ? true : undefined}
        aria-describedby={errorId}
        className={
          errorId
            ? ERROR_INPUT_STYLE
            : props.inputProps.disabled === true ||
                props.inputProps.readOnly === true
              ? DISABLED_INPUT_STYLE
              : DEFAULT_INPUT_STYLE
        }
        {...props.inputProps}
      />
    </FormItemContainer>
  );
};

interface ISelectProps
  extends Omit<IFormItemContainerProps, 'id' | 'children' | 'disabled'> {
  selectProps: React.InputHTMLAttributes<HTMLSelectElement>;
  options: {value: string; label: string; disabled?: boolean}[];
}

export const Select: React.FC<ISelectProps> = (props) => {
  const fallbackId = useId();
  const id = props.selectProps.id ?? fallbackId;
  const errorId =
    props.errors && props.errors.length > 0 ? `${id}-error` : undefined;
  return (
    <FormItemContainer
      id={id}
      {...props}
      disabled={props.selectProps.disabled || props.selectProps.readOnly}>
      <select
        id={id}
        aria-invalid={errorId ? true : undefined}
        aria-describedby={errorId}
        className={
          errorId
            ? ERROR_INPUT_STYLE
            : props.selectProps.disabled === true ||
                props.selectProps.readOnly === true
              ? DISABLED_INPUT_STYLE
              : DEFAULT_INPUT_STYLE
        }
        {...props.selectProps}>
        {props.options.map((option) => (
          <option
            className="my-1 px-2 py-1 rounded hover:bg-gray-100 disabled:hover:bg-white disabled:text-gray-400"
            key={option.value}
            value={option.value}
            disabled={option.disabled === true}>
            {option.label}
          </option>
        ))}
      </select>
    </FormItemContainer>
  );
};

// A select component that renders as a list of checkboxes instead of a dropdown
interface ICheckboxesProps
  extends Omit<IFormItemContainerProps, 'id' | 'children' | 'disabled'> {
  inputProps: React.InputHTMLAttributes<HTMLInputElement>;
  options: {value: string; label: string}[];
}

export const Checkboxes: React.FC<ICheckboxesProps> = (props) => {
  const fallbackId = useId();
  const id = props.inputProps.id ?? fallbackId;
  const errorId =
    props.errors && props.errors.length > 0 ? `${id}-error` : undefined;

  const {
    defaultValue,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    defaultChecked,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    value,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    checked,
    ...inputPropsWithoutDefaultValue
  } = props.inputProps;

  return (
    <FormItemContainer
      id={id}
      {...props}
      disabled={props.inputProps.disabled || props.inputProps.readOnly}>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {props.options.map((option) => (
          <div key={option.value} className="flex items-center">
            <input
              id={id}
              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-600"
              defaultChecked={
                (defaultValue as string[]).find((dv) => dv === option.value) !==
                undefined
              }
              defaultValue={option.value}
              aria-invalid={errorId ? true : undefined}
              aria-describedby={errorId}
              type="checkbox"
              {...inputPropsWithoutDefaultValue}
            />
            <label
              className={classNames(
                'ml-2 block text-sm font-medium',
                inputPropsWithoutDefaultValue.disabled
                  ? 'text-gray-500'
                  : 'text-gray-900',
              )}>
              {option.label}
            </label>
          </div>
        ))}
      </div>
    </FormItemContainer>
  );
};

export interface IComboBoxProps
  extends Omit<IFormItemContainerProps, 'id' | 'children' | 'disabled'> {
  selectProps: React.InputHTMLAttributes<HTMLInputElement>;
  options: {value: string; label: string; disabled?: boolean}[];
  onChange?: (value: string) => void;
}

export const ComboBox: React.FC<IComboBoxProps> = (props) => {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const fallbackId = useId();
  const id = props.selectProps.id ?? fallbackId;
  const {options} = props;
  const errorId =
    props.errors && props.errors.length > 0 ? `${id}-error` : undefined;

  const {defaultValue, ...inputProps} = props.selectProps;

  const [query, setQuery] = useState('');
  const [selectedOption, setSelectedOption] = useState(
    defaultValue
      ? props.options.find((option) => option.value === defaultValue) ?? null
      : null,
  );

  const filteredPeople =
    query === ''
      ? options
      : options.filter((person) => {
          return person.label.toLowerCase().includes(query.toLowerCase());
        });

  useEffect(() => {
    setSelectedOption(
      (prev) => options.find((option) => option.value === prev?.value) ?? null,
    );
  }, [options]);

  const onOptionSelected = (option: {value: string; label: string}) => {
    if (selectedOption?.value !== option.value) {
      setSelectedOption(option);
      if (props.onChange) {
        props.onChange(option.value);
      }
    }
  };

  return (
    <FormItemContainer
      id={id}
      {...props}
      disabled={props.selectProps.disabled || props.selectProps.readOnly}>
      <Combobox as="div" value={selectedOption} onChange={onOptionSelected}>
        <div className="relative mt-2">
          <input
            id={id}
            ref={inputRef}
            {...inputProps}
            value={
              selectedOption?.value ?? props.selectProps.defaultValue ?? ''
            }
            readOnly={true}
            type="hidden"
          />
          <ComboboxInput
            className="w-full rounded-md border-0 bg-white py-1.5 pl-3 pr-10 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
            id={`${id}-label`}
            aria-invalid={errorId ? true : undefined}
            aria-describedby={errorId}
            onChange={(event) => setQuery(event.target.value)}
            displayValue={
              ((option: {value: string; label: string}) => option?.label) as any
            }
          />
          <ComboboxButton className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
            <ChevronUpDownIcon
              className="h-5 w-5 text-gray-400"
              aria-hidden="true"
            />
          </ComboboxButton>

          {filteredPeople.length > 0 ? (
            <ComboboxOptions className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
              {filteredPeople.map((option) => (
                <ComboboxOption
                  key={option.value}
                  value={option}
                  disabled={option.disabled === true}
                  className={({active}) =>
                    classNames(
                      'relative cursor-default select-none py-2 pl-3 pr-9',
                      active ? 'bg-blue-600 text-white' : 'text-gray-900',
                    )
                  }>
                  {({active, selected, disabled}) => (
                    <>
                      <span
                        className={classNames(
                          'block truncate',
                          selected ? 'font-semibold' : '',
                          disabled ? 'opacity-50' : '',
                        )}>
                        {option.label}
                      </span>

                      {disabled ? (
                        <span
                          className={classNames(
                            'absolute inset-y-0 right-0 flex items-center pr-4',
                            active ? 'text-white' : 'text-blue-600',
                          )}>
                          <Badge
                            label={'Disabled'}
                            category="danger"
                            size="small"
                          />
                        </span>
                      ) : null}

                      {selected ? (
                        <span
                          className={classNames(
                            'absolute inset-y-0 right-0 flex items-center pr-4',
                            active ? 'text-white' : 'text-blue-600',
                          )}>
                          <CheckIcon className="h-5 w-5" aria-hidden="true" />
                        </span>
                      ) : null}
                    </>
                  )}
                </ComboboxOption>
              ))}
            </ComboboxOptions>
          ) : null}
        </div>
      </Combobox>
    </FormItemContainer>
  );
};

export interface IFormItemContainerProps extends IFormSectionItemProps {
  id: string;
  label?: ReactNode;
  subLabel?: ReactNode;
  disabled?: boolean;
  horizontal?: boolean;
  errors?: string[];
}

export const FormItemContainer: React.FC<IFormItemContainerProps> = (props) => {
  const {disabled = false, horizontal = false} = props;

  return (
    <FormSectionItem columnSpan={props.columnSpan}>
      <div
        className={classNames(
          'inline-flex',
          horizontal
            ? 'flex-row-reverse items-center gap-x-4'
            : 'w-full flex-col',
        )}>
        {props.label ? (
          <label
            htmlFor={props.id}
            className={classNames(
              'block text-sm font-medium leading-6',
              disabled ? 'text-gray-500' : 'text-gray-900',
            )}>
            {props.label}
          </label>
        ) : null}
        <div className={classNames(horizontal ? 'mt-0' : 'mt-2')}>
          {props.children}
        </div>
        {props.subLabel && !horizontal ? (
          <div
            className={classNames(
              'mt-2 text-sm',
              disabled ? 'text-gray-500' : 'text-gray-600',
            )}>
            {props.subLabel}
          </div>
        ) : null}
        {!horizontal && (
          <p
            className="mt-2 text-sm text-red-600 empty:before:inline-block"
            id={`${props.id}-error`}>
            {props.errors && props.errors.length > 0 ? props.errors[0] : ''}
          </p>
        )}
      </div>
    </FormSectionItem>
  );
};
